import { useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale,
} from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-moment";
import ContentCopyRoundedIcon from "@mui/icons-material/ContentCopyRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import IconButton from "@mui/material/IconButton";
import MaterialTooltip from "@mui/material/Tooltip";
import classes from "./HistoricalEvolutionGraph.module.css";
import { useHistoricalDataContext } from "../../HistoricalDataContext";
import { ChartScaleNotice } from "./ChartScaleNotice";
import {
  canUseLogarithmicScale,
  hasValuesOutsideRange,
  type ChartScaleMode,
} from "./chartScale";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LogarithmicScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  TimeScale,
  TimeSeriesScale,
  zoomPlugin
);

export interface ContextSeriesPoint {
  value: number;
  recvTime: Date;
  timestamp?: Date;
}

export interface ContextSeries {
  contextId: string;
  contextLabel: string;
  color: string;
  data: ContextSeriesPoint[];
  unit?: string;
  attributeLabel?: string;
  yAxisRange?: { min: number; max: number }; // Rango fijo del eje Y
}

interface MultiContextHistoricalChartProps {
  title: string;
  unit?: string;
  series: ContextSeries[];
  height?: number;
  multiAxis?: boolean;
}

const hexToRgba = (hex: string, alpha: number) => {
  const cleanHex = hex.replace("#", "");
  const bigint = parseInt(cleanHex, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};


const buildSeriesCsv = (
  title: string,
  unit: string | undefined,
  series: ContextSeries[],
  multiAxis: boolean
) => {
  const rows = [
    ["contexto", "serie", "timestamp", "valor", "unidad"],
    ...series.flatMap((ctx) =>
      ctx.data.map((point) => [
        ctx.contextLabel,
        multiAxis ? ctx.attributeLabel || ctx.unit || title : title,
        new Date(point.timestamp || point.recvTime).toISOString(),
        point.value,
        ctx.unit || unit || "",
      ])
    ),
  ];

  return rows
    .map((row) => row.map((value) => `"${String(value).replace(/"/g, '""')}"`).join(","))
    .join("\r\n");
};


export const MultiContextHistoricalChart = ({
  title,
  unit,
  series,
  height = 320,
  multiAxis = false,
}: MultiContextHistoricalChartProps) => {
  const { setAlertStartDate, setAlertEndDate, setGeneralStartDate, setGeneralEndDate, generalStartDate, generalEndDate } =
    useHistoricalDataContext();
  const chartRef = useRef<any>(null);
  const [renderKey, setRenderKey] = useState(0);
  const [scaleMode, setScaleMode] = useState<ChartScaleMode>("fixed");
  const titleWithUnit = useMemo(
    () => (unit && title ? `${title} (${unit})` : title),
    [title, unit]
  );
  const [copyStatus, setCopyStatus] = useState<"idle" | "copied" | "error">("idle");

  const visibleSeries = useMemo(
    () =>
      series.map((ctx) => ({
        ...ctx,
        data: ctx.data.filter((point) => {
          const pointDate = new Date(point.timestamp || point.recvTime);
          return pointDate >= generalStartDate && pointDate <= generalEndDate;
        }),
      })),
    [series, generalStartDate, generalEndDate]
  );

  const scaleAssessment = useMemo(() => {
    const scaleSeries = multiAxis ? visibleSeries : visibleSeries.slice(0, 1);
    const constrainedSeries = scaleSeries.filter((ctx) => ctx.yAxisRange);
    const values = scaleSeries.flatMap((ctx) =>
      ctx.data.map((point) => point.value).filter(Number.isFinite)
    );
    const clipped = constrainedSeries.some((ctx) =>
      hasValuesOutsideRange(
        ctx.data.map((point) => point.value).filter(Number.isFinite),
        ctx.yAxisRange
      )
    );

    return {
      clipped,
      canLogarithmicScale: clipped && canUseLogarithmicScale(values),
    };
  }, [multiAxis, visibleSeries]);

  useEffect(() => {
    if (scaleMode === "logarithmic" && !scaleAssessment.canLogarithmicScale) {
      setScaleMode("fixed");
    }
  }, [scaleAssessment.canLogarithmicScale, scaleMode]);

  useEffect(() => {
    setScaleMode("fixed");
  }, [multiAxis, title]);

  const visiblePointCount = visibleSeries.reduce((count, ctx) => count + ctx.data.length, 0);
  const axisIds = useMemo(() => {
    const firstAxisByUnit = new Map<string, string>();
    return series.map((ctx, index) => {
      const unitKey = ctx.unit || `series-${index}`;
      const existingAxisId = firstAxisByUnit.get(unitKey);
      if (existingAxisId) return existingAxisId;
      const axisId = `y${index}`;
      firstAxisByUnit.set(unitKey, axisId);
      return axisId;
    });
  }, [series]);

  const handleCopyCsv = async () => {
    if (visiblePointCount === 0) {
      setCopyStatus("error");
      return;
    }

    try {
      await navigator.clipboard.writeText(buildSeriesCsv(title, unit, visibleSeries, multiAxis));
      setCopyStatus("copied");
    } catch {
      setCopyStatus("error");
    }
  };


  const datasets = useMemo(
    () =>
      visibleSeries.map((ctx, index) => ({
        label: multiAxis ? ctx.attributeLabel || ctx.unit || '' : ctx.contextLabel,
        data: ctx.data.map((point) => ({
          x: new Date(point.timestamp || point.recvTime), // Usar timestamp si está disponible
          y: point.value,
        })),
        borderColor: ctx.color,
        backgroundColor: hexToRgba(ctx.color, 0.2),
        spanGaps: true,
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.2,
        yAxisID: multiAxis ? axisIds[index] : 'y',
      })),
    [axisIds, visibleSeries, multiAxis]
  );

  const options = useMemo(() => {
    const baseScales = {
      x: {
        type: "time" as const,
        time: {
          tooltipFormat: "DD MMM YYYY HH:mm",
          displayFormats: {
            minute: "DD MMM HH:mm",
            hour: "DD MMM HH:mm",
            day: "DD MMM",
            month: "MMM YYYY",
          },
        },
        ticks: {
          autoSkip: true,
          maxTicksLimit: 10,
        },
      },
    };

    let yScales: any = {};

    if (multiAxis && series.length > 0) {
      const axisSeries = series.filter((_, index) => axisIds[index] === `y${index}`);
      axisSeries.forEach((ctx, index) => {
        const position: "left" | "right" = index % 2 === 0 ? "left" : "right";
        const axisId = axisIds[series.indexOf(ctx)];
        yScales[axisId] = {
          type: scaleMode === "logarithmic" ? "logarithmic" : "linear",
          display: true,
          position,
          title: {
            display: true,
            text: ctx.unit || "",
          },
          grid: {
            drawOnChartArea: index === 0,
          },
          ...(scaleMode === "fixed" && ctx.yAxisRange ? {
            min: ctx.yAxisRange.min,
            max: ctx.yAxisRange.max,
          } : {}),
        };
      });
    } else {
      // Modo single-axis: usar el rango del primer serie si existe
      const firstSeries = series[0];
      yScales = {
        y: {
          type: scaleMode === "logarithmic" ? "logarithmic" : "linear",
          display: true,
          position: "left" as const,
          ...(scaleMode === "fixed" && firstSeries?.yAxisRange ? {
            min: firstSeries.yAxisRange.min,
            max: firstSeries.yAxisRange.max,
          } : {}),
        },
      };
    }

    return {
      maintainAspectRatio: false,
      responsive: true,
      interaction: {
        mode: "index" as const,
        intersect: false,
      },
      plugins: {
        legend: {
          position: "bottom" as const,
        },
        title: {
          display: Boolean(titleWithUnit),
          text: titleWithUnit,
        },
        tooltip: {
          callbacks: {
            label: (tooltipItem: any) => {
              const value = tooltipItem.parsed.y;
              const datasetIndex = tooltipItem.datasetIndex;
              const ctx = series[datasetIndex];
              if (multiAxis) {
                const label = ctx?.attributeLabel || "";
                const unitStr = ctx?.unit ? ` ${ctx.unit}` : "";
                return `${label}: ${value}${unitStr}`;
              } else {
                const label = ctx?.contextLabel || tooltipItem.dataset.label || "";
                const unitStr = unit ? ` ${unit}` : "";
                return `${label}: ${value}${unitStr}`;
              }
            },
          },
        },
        zoom: {
          pan: { enabled: false },
          zoom: {
            drag: {
              enabled: true,
              backgroundColor: "rgba(0, 123, 255, 0.2)",
              borderColor: "rgba(0, 123, 255, 0.8)",
              borderWidth: 1,
            },
            mode: "x" as const,
            onZoomComplete: ({ chart }: any) => {
              const xScale = chart.scales.x;
              const start = new Date(xScale.min);
              const end = new Date(xScale.max);
              setAlertStartDate(start);
              setAlertEndDate(end);
              setGeneralStartDate(start);
              setGeneralEndDate(end);
              setRenderKey((k) => k + 1); // Forzar re-render del gráfico actual
            },
          },
        },
      },
      scales: {
        ...baseScales,
        ...yScales,
      },
    };
  }, [
    setAlertStartDate,
    setAlertEndDate,
    setGeneralStartDate,
    setGeneralEndDate,
    titleWithUnit,
    unit,
    multiAxis,
    axisIds,
    series,
    scaleMode,
    setRenderKey,
  ]);

  const data = useMemo(
    () => ({
      datasets,
    }),
    [datasets]
  );

  return (
    <div className={classes.graphContainer} style={{ height }}>
      <div className={classes.graphToolbar}>
        {copyStatus !== "idle" && (
          <span className={classes.copyStatus} role="status" aria-live="polite">
            {copyStatus === "copied" ? "CSV copiado" : "No se pudo copiar"}
          </span>
        )}
        <MaterialTooltip
          title={copyStatus === "error" ? "Reintentar copia CSV" : "Copiar CSV de la serie visible"}
        >
          <span>
            <IconButton
              size="small"
              onClick={handleCopyCsv}
              disabled={visiblePointCount === 0}
              aria-label="Copiar CSV de la serie visible"
            >
              {copyStatus === "copied" ? (
                <CheckRoundedIcon fontSize="small" />
              ) : (
                <ContentCopyRoundedIcon fontSize="small" />
              )}
            </IconButton>
          </span>
        </MaterialTooltip>
      </div>
      <ChartScaleNotice
        clipped={scaleAssessment.clipped}
        canLogarithmicScale={scaleAssessment.canLogarithmicScale}
        scaleMode={scaleMode}
        onScaleModeChange={setScaleMode}
      />
      <Line key={renderKey} ref={chartRef} options={options} data={data} />
    </div>
  );
};

export default MultiContextHistoricalChart;
